import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Upload, Image as ImageIcon, X, Save, Lock } from 'lucide-react';
import ContactSection from '../components/ContactSection';
import { useAuth } from '../context/AuthContext';
import { fetchWeekEntries, saveWeekEntries, fetchPhotos, uploadPhoto, deletePhoto, fileUrl } from '../lib/api';

const WeekDetailPage = ({ practicumType }) => {
  const { weekNumber } = useParams();
  const navigate = useNavigate();
  const week = parseInt(weekNumber);
  const totalWeeksForType = practicumType === 'i' ? 4 : 8;
  const { isOwner } = useAuth();

  // Validate week number - redirect if invalid
  useEffect(() => {
    if (isNaN(week) || week < 1 || week > totalWeeksForType) {
      navigate(practicumType === 'i' ? '/practicum-i' : '/practicum-ii', { replace: true });
    }
  }, [week, totalWeeksForType, practicumType, navigate]);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  // Calculate dates based on practicum type and week number
  const getDayDate = (dayIndex) => {
    if (practicumType === 'i') {
      // Practicum I starts Monday 3 August 2026
      const startDay = 3; // 3rd August 2026
      const dayNum = startDay + (week - 1) * 7 + dayIndex;
      const suffix = (n) => {
        if (n >= 11 && n <= 13) return 'th';
        switch (n % 10) {
          case 1: return 'st';
          case 2: return 'nd';
          case 3: return 'rd';
          default: return 'th';
        }
      };
      return `${dayNum}${suffix(dayNum)} August 2026`;
    }
    // For Practicum II, use week number only (dates unknown)
    return `Day ${dayIndex + 1}`;
  };

  const [entries, setEntries] = useState({});
  const [images, setImages] = useState({});
  const [savedNotification, setSavedNotification] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingDay, setUploadingDay] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [entriesData, ...photosPerDay] = await Promise.all([
        fetchWeekEntries(practicumType, week),
        ...days.map((day) => fetchPhotos(`week:${practicumType}:${week}:${day}`)),
      ]);
      setEntries(entriesData);
      const imagesMap = {};
      days.forEach((day, i) => {
        if (photosPerDay[i].length > 0) imagesMap[day] = photosPerDay[i];
      });
      setImages(imagesMap);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practicumType, week]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEntryChange = (day, value) => {
    setEntries((prev) => ({ ...prev, [day]: value }));
  };

  const handleImageUpload = async (day, event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) return;
    setUploadingDay(day);
    try {
      const photo = await uploadPhoto(`week:${practicumType}:${week}:${day}`, file);
      setImages((prev) => ({ ...prev, [day]: [...(prev[day] || []), photo] }));
    } catch (e) {
      alert(e.message || 'Upload failed. Please try again.');
    } finally {
      setUploadingDay(null);
    }
  };

  const removeImage = async (day, photo) => {
    try {
      await deletePhoto(photo.id);
      setImages((prev) => ({ ...prev, [day]: prev[day].filter((p) => p.id !== photo.id) }));
    } catch (e) {
      alert(e.message || 'Could not delete photo.');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveWeekEntries(practicumType, week, entries);
      setSavedNotification(true);
      setTimeout(() => setSavedNotification(false), 2000);
    } catch (e) {
      alert(e.message || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const practicumName = practicumType === 'i' ? 'Practicum I' : 'Practicum II';
  const backPath = practicumType === 'i' ? '/practicum-i' : '/practicum-ii';
  const totalWeeks = totalWeeksForType;
  const accentColor = practicumType === 'i' ? 'var(--ochre)' : 'var(--orange)';

  // Early return if week is invalid to prevent rendering errors
  if (isNaN(week) || week < 1 || week > totalWeeksForType) {
    return null;
  }

  return (
    <>
      <section
        className="relative py-32 md:py-48 px-6 md:px-10 min-h-screen"
        style={{
          background: 'linear-gradient(135deg, #faf7f2 0%, #f5ebe0 100%)',
          paddingTop: '120px',
        }}
      >
        {/* Save Notification */}
        <AnimatePresence>
          {savedNotification && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="fixed top-24 right-6 z-50 px-6 py-3 rounded-full shadow-lg flex items-center gap-2"
              style={{ background: 'var(--ochre)', color: 'var(--paper)' }}
              data-testid="save-notification"
            >
              <Save size={16} />
              <span className="font-body text-sm font-medium">Saved!</span>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="max-w-5xl mx-auto">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <Link to={backPath}>
              <motion.button
                whileHover={{ x: -5 }}
                className="flex items-center gap-2 font-body text-sm uppercase tracking-widest text-quiet hover:text-ochre transition-colors"
              >
                <ArrowLeft size={18} />
                Back to {practicumName}
              </motion.button>
            </Link>
          </motion.div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <div className="font-body text-xs uppercase tracking-widest mb-4" style={{ color: accentColor }}>
              {practicumName} · Week {week} of {totalWeeks}
            </div>
            <h1
              className="font-display font-black text-5xl md:text-7xl lg:text-8xl leading-none tracking-tight mb-6"
              style={{ color: 'var(--ink)' }}
            >
              Week{' '}
              <span style={{ color: accentColor }}>{String(week).padStart(2, '0')}</span>
            </h1>
            <p className="font-body text-lg md:text-xl leading-relaxed max-w-3xl text-quiet">
              Five working days documented — reflections, photographs, and the essence of each day.
            </p>
          </motion.div>

          {/* Progress Bar */}
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="mb-12 relative h-2 rounded-full overflow-hidden"
            style={{ background: 'rgba(207, 91, 29, 0.1)', transformOrigin: 'left' }}
          >
            <motion.div
              initial={{ width: '0%' }}
              animate={{ width: `${(week / totalWeeks) * 100}%` }}
              transition={{ duration: 1.2, delay: 0.5, ease: 'easeOut' }}
              className="absolute inset-y-0 left-0 rounded-full"
              style={{ background: accentColor }}
            />
          </motion.div>

          {/* Days Grid */}
          <div className="space-y-8">
            {days.map((day, index) => (
              <motion.div
                key={day}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="rounded-2xl shadow-lg overflow-hidden"
                style={{
                  background: 'rgba(250, 247, 242, 0.95)',
                  border: `2px solid ${accentColor === 'var(--ochre)' ? 'rgba(207, 91, 29, 0.2)' : 'rgba(244, 164, 96, 0.3)'}`,
                }}
                data-testid={`day-card-${day.toLowerCase()}`}
              >
                <div className="p-6 md:p-8">
                  {/* Day Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center font-display font-black text-xl"
                        style={{ background: accentColor, color: 'var(--paper)' }}
                      >
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="font-display font-bold text-2xl md:text-3xl" style={{ color: 'var(--ink)' }}>
                          {day}
                        </h3>
                        {practicumType === 'i' && (
                          <div className="font-body text-sm mt-1" style={{ color: 'var(--ochre)' }}>
                            {getDayDate(index)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Diary Entry Textarea */}
                  <div className="mb-6">
                    <label className="font-body text-xs uppercase tracking-widest text-quiet mb-2 block">
                      Journal Entry
                    </label>
                    {isOwner ? (
                      <textarea
                        value={entries[day] || ''}
                        onChange={(e) => handleEntryChange(day, e.target.value)}
                        placeholder="Write what you did, what you learned, and any important reflection here..."
                        rows={6}
                        className="w-full p-4 rounded-lg font-body text-base leading-relaxed resize-none focus:outline-none focus:ring-2 transition-all"
                        style={{
                          background: 'rgba(255, 215, 186, 0.15)',
                          border: '1px solid var(--rule)',
                          color: 'var(--ink)',
                        }}
                        data-testid={`journal-entry-${day.toLowerCase()}`}
                      />
                    ) : (
                      <div
                        className="w-full p-4 rounded-lg font-body text-base leading-relaxed whitespace-pre-wrap min-h-[3.5rem]"
                        style={{
                          background: 'rgba(255, 215, 186, 0.1)',
                          border: '1px solid var(--rule)',
                          color: entries[day] ? 'var(--ink)' : 'var(--quiet)',
                        }}
                        data-testid={`journal-entry-${day.toLowerCase()}`}
                      >
                        {entries[day] || (loading ? 'Loading…' : 'No entry yet.')}
                      </div>
                    )}
                  </div>

                  {/* Images */}
                  <div>
                    <label className="font-body text-xs uppercase tracking-widest text-quiet mb-3 block">
                      Photos
                    </label>

                    {images[day] && images[day].length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        {images[day].map((photo, i) => (
                          <motion.div
                            key={photo.id}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="relative aspect-square rounded-lg overflow-hidden group"
                          >
                            <img
                              src={fileUrl(photo.url)}
                              alt={`${day} entry ${i + 1}`}
                              className="w-full h-full object-cover"
                            />
                            {isOwner && (
                              <button
                                onClick={() => removeImage(day, photo)}
                                className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                style={{ background: 'rgba(0, 0, 0, 0.6)', color: 'white' }}
                                data-testid={`remove-photo-${day.toLowerCase()}-${i}`}
                              >
                                <X size={14} />
                              </button>
                            )}
                          </motion.div>
                        ))}
                      </div>
                    )}

                    {isOwner && (
                      <label
                        className={`cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full font-body text-sm font-medium transition-all hover:shadow-md ${uploadingDay === day ? 'opacity-60 pointer-events-none' : ''}`}
                        style={{
                          background: 'rgba(207, 91, 29, 0.1)',
                          border: `1px dashed ${accentColor}`,
                          color: accentColor,
                        }}
                      >
                        <Upload size={16} />
                        <span>{uploadingDay === day ? 'Uploading…' : 'Add Photo'}</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleImageUpload(day, e)}
                          data-testid={`upload-photo-${day.toLowerCase()}`}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Save Button (owner only) */}
          {isOwner ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="mt-12 text-center"
            >
              <motion.button
                onClick={handleSave}
                disabled={saving}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="px-10 py-4 rounded-full font-body font-medium uppercase tracking-widest text-sm shadow-lg disabled:opacity-60"
                style={{ background: accentColor, color: 'var(--paper)' }}
                data-testid="save-week-entries"
              >
                <span className="flex items-center gap-2">
                  <Save size={18} />
                  {saving ? 'Saving…' : 'Save All Entries'}
                </span>
              </motion.button>
              <p className="font-body text-xs text-quiet mt-4 italic">
                Photos save instantly when uploaded · Click save to store your written entries
              </p>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="mt-12 flex items-center justify-center gap-2 font-body text-xs uppercase tracking-widest text-quiet"
            >
              <Lock size={12} />
              Read-only — only the owner can edit this portfolio
            </motion.div>
          )}

          {/* Week Navigation */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="mt-16 flex flex-col sm:flex-row justify-between gap-4"
          >
            {week > 1 ? (
              <Link to={`/practicum-${practicumType}/week/${week - 1}`}>
                <motion.button
                  whileHover={{ x: -5 }}
                  className="flex items-center gap-2 font-body text-sm uppercase tracking-widest text-quiet hover:text-ochre transition-colors"
                >
                  <ArrowLeft size={18} />
                  Previous Week
                </motion.button>
              </Link>
            ) : <div />}

            {week < totalWeeks ? (
              <Link to={`/practicum-${practicumType}/week/${week + 1}`}>
                <motion.button
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-2 font-body text-sm uppercase tracking-widest transition-colors"
                  style={{ color: accentColor }}
                >
                  Next Week
                  <ArrowLeft size={18} className="rotate-180" />
                </motion.button>
              </Link>
            ) : <div />}
          </motion.div>
        </div>
      </section>
      <ContactSection showMap={practicumType === 'i'} />
    </>
  );
};

export default WeekDetailPage;
